import React from "react";

import {
  Box,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import Autocomplete from "@material-ui/lab/Autocomplete";
import parse from "autosuggest-highlight/parse";

import {
  AddressComponents,
  AutocompletePrediction,
  useMerchantModel,
  usePlacePredictions,
} from "client/hooks";
import {
  Field,
  FieldMap,
  FieldSet,
  FieldSets,
  Merchant,
} from "client/lib/apt-merchant";

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  helperText: {
    fontSize: `${14 / 16}rem`,
  },
  manualEntry: {
    alignItems: "center",
    display: "flex",
    minHeight: "45px",
  },
  wrapper: {
    background: theme.palette.common.white,
  },
}));

const getFieldMapToUpdate = (
  fieldSet: FieldSet<any>,
  merchant: Merchant
): FieldMap<any> => {
  if (fieldSet instanceof FieldSets.StoreLocation) {
    return merchant.locations[fieldSet.locationIndex].fieldMap;
  } else if (fieldSet instanceof FieldSets.BusinessOwner) {
    return merchant.owners[fieldSet.ownerIndex].fieldMap;
  }
  return fieldSet.fieldMap;
};

type AddressFieldNames = {
  addressFieldName: string;
  cityFieldName: string;
  postalCodeFieldName: string;
  stateFieldName: string;
};

const getAddressFieldNamesToUpdate = (
  fieldSet: FieldSet<any>
): AddressFieldNames => {
  if (fieldSet instanceof FieldSets.StoreLocation) {
    return {
      addressFieldName: "locationStreet",
      cityFieldName: "locationCity",
      postalCodeFieldName: "locationPostalCode",
      stateFieldName: "locationState",
    };
  } else if (fieldSet instanceof FieldSets.BusinessOwner) {
    return {
      addressFieldName: "street",
      cityFieldName: "city",
      postalCodeFieldName: "postalCode",
      stateFieldName: "state",
    };
  }

  return {
    addressFieldName: "businessStreet",
    cityFieldName: "businessCity",
    postalCodeFieldName: "businessPostalCode",
    stateFieldName: "businessState",
  };
};

const updateMerchantAddressFields = (
  addressComponents: AddressComponents,
  fieldSet: FieldSet<any>,
  merchant: Merchant
): boolean => {
  let shouldUpdateMerchant = false;
  const { city, state, postalCode, address } = addressComponents;

  const fieldMap = getFieldMapToUpdate(fieldSet, merchant);
  const {
    addressFieldName,
    cityFieldName,
    postalCodeFieldName,
    stateFieldName,
  } = getAddressFieldNamesToUpdate(fieldSet);

  if (city && fieldMap[cityFieldName].value !== city) {
    shouldUpdateMerchant = true;
    fieldMap[cityFieldName].value = city;
  }

  if (postalCode && fieldMap[postalCodeFieldName].value !== postalCode) {
    shouldUpdateMerchant = true;
    fieldMap[postalCodeFieldName].value = postalCode;
  }

  if (address && fieldMap[addressFieldName].value !== address) {
    shouldUpdateMerchant = true;
    fieldMap[addressFieldName].value = address;
  }

  if (state && fieldMap[stateFieldName].value !== state) {
    shouldUpdateMerchant = true;
    fieldMap[stateFieldName].value = state;
  }

  return shouldUpdateMerchant;
};

type Props = {
  error: string;
  fieldSet: FieldSet<any>;
  field: Field<any>;
  helperText?: string;
  updateFieldModel: (value: string) => void;
};

export const PlacesField: React.FC<Props> = ({
  error,
  fieldSet,
  field,
  helperText,
  updateFieldModel,
}) => {
  const [inputValue, setInputValue] = React.useState(field?.value ?? "");
  const classes = useStyles();
  const { merchant, syncStoreWithMerchant } = useMerchantModel();

  const {
    addressComponents,
    placeValue,
    placePredictions,
    setAddressComponents,
    setPlaceValue,
    setPlacePredictions,
  } = usePlacePredictions(inputValue, field?.value);

  React.useEffect(() => {
    // update local input state from field value
    if (!placeValue) {
      setInputValue(field.value ?? "");
    }
  }, [field.value, placeValue]);

  React.useEffect(() => {
    // update the merchant model when the user chooses a place selection
    if (addressComponents) {
      const shouldUpdateMerchant = updateMerchantAddressFields(
        addressComponents,
        fieldSet,
        merchant
      );
      if (shouldUpdateMerchant) {
        syncStoreWithMerchant();
        // clear address components so user can update individual fields
        setAddressComponents(null);
      }
    }
  }, [
    addressComponents,
    fieldSet,
    merchant,
    syncStoreWithMerchant,
    setAddressComponents,
  ]);

  const clearAddressField = () => {
    if (addressComponents?.address) {
      setAddressComponents({ address: "" });
    }
  };

  const clearPredictionsAndInput = () => {
    setInputValue("");
    setPlaceValue(null);
    setPlacePredictions([]);
    updateFieldModel("");
    clearAddressField();
  };

  return (
    <Autocomplete
      filterOptions={(x) => x}
      filterSelectedOptions
      freeSolo
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : option.structured_formatting.main_text
      }
      id={`${fieldSet.fieldSetName}-autocomplete`}
      includeInputInList
      inputValue={inputValue}
      onChange={(_: any, newValue: AutocompletePrediction | null | string) => {
        if (typeof newValue === "string") {
          // don't update autocomplete components if user chooses to manually enter address (we update model on blur)
          return;
        }
        setPlacePredictions(
          newValue ? [newValue, ...placePredictions] : placePredictions
        );
        setPlaceValue(newValue);
      }}
      onInputChange={(_, newInputValue: string, reason) => {
        if (reason === "clear") {
          clearPredictionsAndInput();
        } else {
          setInputValue(newInputValue);
        }
      }}
      onBlur={() => {
        if (inputValue) {
          updateFieldModel(inputValue);
          clearAddressField();
        }
      }}
      options={placePredictions}
      renderInput={(params) => (
        <TextField
          {...params}
          className={classes.wrapper}
          label={field.i18nValues.label}
          variant="outlined"
          fullWidth
          autoComplete="off"
          error={Boolean(error)}
          helperText={helperText}
        />
      )}
      renderOption={(option) => {
        if (typeof option === "string") {
          // will only ever be the manually typed value
          return (
            <Box className={classes.manualEntry}>
              <Typography color="textSecondary">
                <strong>{option}</strong>
              </Typography>
            </Box>
          );
        }

        const matches =
          option?.structured_formatting?.main_text_matched_substrings ?? [];
        // highlight hovered selections
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: { offset: number; length: number }) => [
            match.offset,
            match.offset + match.length,
          ])
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{ fontWeight: part.highlight ? 700 : 400 }}
                >
                  {part.text}
                </span>
              ))}
              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
      value={placeValue}
    />
  );
};
